<script setup>
// @ts-nocheck
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
    required: true
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Select...'
  },
  sectionHeaders: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  },
  width: {
    type: String,
    default: 'auto'
  },
  maxWidth: {
    type: String,
    default: 'none'
  },
  icon: {
    type: String,
    default: 'filter' // 'filter', 'sort', 'none'
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const isOpen = ref(false)
const menuRef = ref(null)
const containerRef = ref(null)
const buttonRef = ref(null)
const highlightedIndex = ref(-1)

// Viewport boundary detection
const menuStyle = ref({})

const selectedOption = computed(() => {
  return props.options.find(opt => opt.value === props.modelValue)
})

const selectedLabel = computed(() => {
  return selectedOption.value?.label || props.placeholder
})

const flatOptions = computed(() => {
  return props.options.map(opt => opt.value)
})

const calculatePosition = async () => {
  await nextTick()

  if (!menuRef.value || !buttonRef.value) return

  const button = buttonRef.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const viewportWidth = window.innerWidth
  const menuHeight = 320 // max-height from template
  const menuWidth = Math.max(button.width, 180)

  // Default: below the button, left-aligned
  let top = button.bottom + 8 // mt-2 = 8px
  let left = button.left

  // Check if menu would overflow bottom of viewport
  if (button.bottom + 8 + menuHeight > viewportHeight - 16) {
    // Flip to above the button
    top = button.top - 8 - menuHeight

    // Also ensure we don't overflow top
    if (top < 16) {
      top = 16 // Keep some padding from top
    }
  }

  // Check if menu would overflow right edge of viewport
  if (left + menuWidth > viewportWidth - 16) {
    // Align to right edge of button
    left = viewportWidth - menuWidth - 16
  }

  // Check if menu would overflow left edge of viewport
  if (left < 16) {
    left = 16 // Keep some padding from left
  }

  menuStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    width: `${menuWidth}px`
  }
}

const openMenu = async () => {
  if (props.disabled) return
  isOpen.value = true
  highlightedIndex.value = flatOptions.value.indexOf(props.modelValue)
  await nextTick()
  calculatePosition()
  focusHighlighted()
}

const closeMenu = () => {
  isOpen.value = false
  highlightedIndex.value = -1
  menuStyle.value = {}
}

const selectOption = (option) => {
  emit('update:modelValue', option.value)
  emit('change', option)
  closeMenu()
  buttonRef.value?.focus()
}

const handleKeydown = (event) => {
  if (!isOpen.value) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault()
      openMenu()
    }
    return
  }

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      highlightNext()
      break
    case 'ArrowUp':
      event.preventDefault()
      highlightPrev()
      break
    case 'Enter':
    case ' ':
      event.preventDefault()
      if (highlightedIndex.value >= 0) {
        const option = props.options[highlightedIndex.value]
        selectOption(option)
      }
      break
    case 'Escape':
      event.preventDefault()
      closeMenu()
      buttonRef.value?.focus()
      break
    case 'Tab':
      closeMenu()
      break
  }
}

const highlightNext = () => {
  if (highlightedIndex.value < flatOptions.value.length - 1) {
    highlightedIndex.value++
    focusHighlighted()
  }
}

const highlightPrev = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
    focusHighlighted()
  }
}

const focusHighlighted = () => {
  const items = menuRef.value?.querySelectorAll('[role="option"]')
  items?.[highlightedIndex.value]?.focus()
}

const handleClickOutside = (event) => {
  if (!containerRef.value?.contains(event.target)) {
    closeMenu()
  }
}

// Handle scroll and resize to recalculate position
const handleScrollOrResize = () => {
  if (isOpen.value) {
    calculatePosition()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', handleScrollOrResize, true)
  window.addEventListener('resize', handleScrollOrResize)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleScrollOrResize, true)
  window.removeEventListener('resize', handleScrollOrResize)
})

// Recalculate position when options change
watch(() => props.options, () => {
  if (isOpen.value) {
    nextTick(() => calculatePosition())
  }
})

const getSectionForIndex = (index) => {
  let count = 0
  for (let i = 0; i < props.sectionHeaders.length; i++) {
    count += props.sectionHeaders[i].count
    if (index < count) {
      return props.sectionHeaders[i]
    }
  }
  return null
}
</script>

<template>
  <div
    ref="containerRef"
    class="relative min-w-0"
    :style="{ width, maxWidth }"
  >
    <!-- Trigger Button -->
    <button
      ref="buttonRef"
      type="button"
      @click="openMenu"
      @keydown="handleKeydown"
      :disabled="disabled"
      :aria-haspopup="true"
      :aria-expanded="isOpen"
      :aria-label="label"
      class="group flex items-center justify-between gap-1.5 w-full px-4 sm:px-5 py-2 sm:py-2.5 bg-m3-surface-container-high dark:bg-m3-surface-container hover:bg-m3-surface-container-highest dark:hover:bg-m3-surface-variant/50 border border-m3-outline hover:border-m3-primary/50 text-m3-on-surface font-medium text-sm rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-inset focus-visible:ring-m3-primary/20 transition-all duration-200 ease-m3-emphasis disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] sm:min-h-[48px]"
      :class="{
        'bg-m3-surface-variant border-m3-primary shadow-sm': isOpen,
        'hover:shadow-sm': !isOpen
      }"
    >
      <!-- Dynamic Icon based on prop -->
      <svg
        v-if="icon !== 'none'"
        xmlns="http://www.w3.org/2000/svg"
        class="h-4 w-4 text-m3-on-surface-variant/70 group-hover:text-m3-primary transition-colors duration-200 flex-shrink-0"
        :class="{ 'text-m3-primary': isOpen }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
      >
        <path v-if="icon === 'filter'" stroke-linecap="round" stroke-linejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        <path v-else-if="icon === 'sort'" stroke-linecap="round" stroke-linejoin="round" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
      </svg>

      <!-- Selected Label -->
      <span class="truncate flex-1 text-left min-w-0">{{ selectedLabel }}</span>

      <!-- Chevron -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-4 w-4 text-m3-on-surface-variant/60 group-hover:text-m3-primary transition-transform duration-200 ease-m3-standard flex-shrink-0"
        :class="{ 'rotate-180 text-m3-primary': isOpen }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2.5"
      >
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Menu Dropdown - Material 3: positioned to stay within viewport bounds -->
    <!-- Menu Dropdown - Material 3: positioned to stay within viewport bounds -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-2 scale-95"
        enter-to-class="opacity-100 translate-y-0 scale-100"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0 scale-100"
        leave-to-class="opacity-0 -translate-y-2 scale-95"
      >
        <div
          v-if="isOpen"
          ref="menuRef"
          role="listbox"
          :aria-label="label"
          class="m3-menu-dropdown fixed z-[9999] bg-brand-surface rounded-2xl shadow-xl border border-brand-muted/10 overflow-hidden"
          :style="[menuStyle, { maxHeight: '320px', overflowY: 'auto' }]"
        >
          <!-- Menu Items -->
          <ul class="py-2">
            <li
              v-for="(option, index) in options"
              :key="option.value"
              role="option"
              :aria-selected="option.value === modelValue"
              tabindex="0"
              @click="selectOption(option)"
              @keydown.enter="selectOption(option)"
              @keydown.space.prevent="selectOption(option)"
              @mouseenter="highlightedIndex = index"
              @focusin="highlightedIndex = index"
              class="mx-2 px-4 py-3 rounded-lg cursor-pointer transition-all duration-150 flex items-center gap-3 outline-none"
              :class="[
                option.value === modelValue
                  ? 'bg-brand-accent text-white'
                  : 'text-brand-text hover:bg-brand-muted/10 focus:bg-brand-muted/10',
                highlightedIndex === index && option.value !== modelValue ? 'bg-brand-muted/10' : '',
                index > 0 && getSectionForIndex(index) && getSectionForIndex(index - 1) !== getSectionForIndex(index) ? 'mt-3' : '',
                index < options.length - 1 && getSectionForIndex(index) !== getSectionForIndex(index + 1) ? 'mb-3' : ''
              ]"
            >
              <!-- Icon (if provided) -->
              <span v-if="option.icon" class="flex-shrink-0">
                <component :is="option.icon" class="h-5 w-5" :class="option.value === modelValue ? 'text-white' : 'text-brand-muted'" />
              </span>

              <!-- Label -->
              <span class="flex-1 font-medium">{{ option.label }}</span>

              <!-- Checkmark for selected -->
              <svg
                v-if="option.value === modelValue"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 flex-shrink-0"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
              </svg>
            </li>
          </ul>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
